import React from 'react';

/**
 * A route-split component that can be preloaded and then renders SYNCHRONOUSLY.
 *
 * Why this exists: react-snap prerenders each route by client-rendering it in
 * puppeteer and snapshotting the resulting DOM — so the static HTML contains the
 * full page but NONE of React's Suspense dehydration markers. If the matched
 * route is a plain React.lazy() component, its chunk is not resolved at hydration
 * time, so the first client render is the <Suspense> fallback, which does not match
 * the prerendered content → React hydration error #418/#423 (and react-snap exits
 * non-zero). By preloading the matched route's chunk BEFORE hydrateRoot (see
 * src/index.tsx) and rendering the resolved module synchronously, the first client
 * render equals the prerendered HTML and hydration is clean — while still keeping
 * every route in its own chunk. All route components take no props.
 */
export type PreloadableComponent = React.FC & { preload: () => Promise<void> };

export function lazyWithPreload(
  factory: () => Promise<{ default: React.ComponentType }>
): PreloadableComponent {
  let Component: React.ComponentType | undefined;
  let loading: Promise<void> | undefined;

  const preload = (): Promise<void> => {
    if (!loading) {
      loading = factory().then((mod) => {
        Component = mod.default;
      });
    }
    return loading;
  };

  const Wrapped = (): React.ReactElement => {
    if (!Component) {
      // Not loaded yet: suspend. This path is taken during react-snap's createRoot
      // prerender (which waits for the chunk via network-idle). On the client we
      // preload the matched route before hydrateRoot, so Component is already set
      // here and we never suspend during hydration.
      throw preload();
    }
    return React.createElement(Component);
  };

  const result = Wrapped as unknown as PreloadableComponent;
  result.preload = preload;
  return result;
}
